import React, { useEffect, useState } from 'react';
import axios from 'axios';
import LoadingIcon from 'mdi-react/LoadingIcon';
import PropTypes from 'prop-types';
import Button from 'components/buttons/Button';
import RenderFormInput from 'components/forms/RenderFormInput';
import RenderSelectInput from 'components/forms/RenderSelectInput';
import RenderDateTimePicker from 'components/forms/RenderDateTimePicker';
import InfiniteScrollerSelect from 'components/forms/InfiniteScrollerSelect';
import { requiredValidator, requiredOptionValidator, handleBlur } from 'utils/validators';
import Label from 'components/forms/Label';
import { resultsToOptions } from 'utils/misc';
import { Field, reduxForm, getFormValues, change, formValueSelector } from 'redux-form';
import { compose } from 'redux';
import { connect } from 'react-redux';
import moment from 'moment';
import { withStyles } from '@material-ui/styles';
import { createOpportunity, updateOpportunity } from 'redux/actions/opportunityActions';
import { getMentions } from 'redux/actions/coverageActions';
import { getAccounts } from 'redux/actions/accountActions';
import { getSequences } from 'redux/actions/sequenceActions';
import { getCampaigns } from 'redux/actions/campaignActions';
import { getContacts, getOutlets } from 'redux/actions/contactActions';
import { timelineStepRow } from 'themes/colors';
import { get, isEmpty, last } from 'lodash';
import { STAGE_OPTIONS, getContactLabel, getOutletNameUrl } from 'constants/opportunity';
import { LAST_UPDATED, DATE_FORMAT } from 'constants/config';
import { apiGetMention } from 'constants/apiRouting';
import { useCountContextCountRotting } from 'components/CountRottingLeads';
import SelectedCoverage from './SelectedCoverage';
import ConfirmationModal from '../modals/ConfirmationModal';


const styles = theme => ({
  groupTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.secondary[900],
  },
  lastUpdate: {
    fontSize: '0.700rem',
    fontWeight: 600,
    color: theme.palette.secondary[900],
    textAlign: 'right',
  },
  group: {
    marginTop: '0.75rem',
    padding: '0.75rem',
    borderRadius: '0.375rem',
    background: timelineStepRow,
    marginBottom: '1rem',

    '& > label:first-child': {
      marginTop: 0,
    },
  },
  twoColumnGroup: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '0.75rem',
  },
  selectGroup: {
    flex: 1,
  },
  btnContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '1rem',
    gap: '1rem',
  },
  nestedInput: {
    flex: 1,

    '& > .react-datepicker-wrapper': {
      width: '100%',
    },
  },
  coverageBlock: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0.625rem 0rem',
    gap: '0.5rem',
    background: '#E2E8EE',
    borderRadius: '0.375rem',
    color: '#A9BBCA',
    textAlign: 'center',
  },
  noteStyle: {
    padding: '0rem',
  },
  disable: {
    '& .form-control:disabled': {
      background: '#f2f2f2',
    },
  },
});

const OpportunityForm = ({
  config, initialValues, toggle, createOpportunityAction, editOpportunityAction,
  accounts, outlets, campaigns, sequences, coverages, classes, contacts,
  getAccountsAction, getOutletsAction, getCampaignsAction, getSequencesAction,
  getCoverageAction, getContactsAction, formValues,
  dispatch, invalid, pristine, submitting, selectedCoverage, readOnly,
  isLoading: isLoadingProps,
}) => {
  const isEdit = initialValues.id;
  // eslint-disable-next-line no-unused-vars
  const [showWarning, setShowWarning] = useState(false);

  const handleLoadOutletOptions = async (search) => {
    await getOutletsAction({ ...search, account__id: formValues.account.value });
  };

  const handleLoadContactOptions = async (search) => {
    await getContactsAction({ search });
  };

  const handleLoadAccountOptions = async (search) => {
    await getAccountsAction({ search });
  };

  const handleLoadCampaignOptions = async (search) => {
    await getCampaignsAction({ search, account__id: formValues.account.value });
  };

  const handleLoadSequenceOptions = async (search) => {
    await getSequencesAction(formValues.campaign.value, { search });
  };

  const handleLoadCoverageOptions = async (search) => {
    await getCoverageAction({ ...search, account__id: formValues.account.value, has_opportunity: false });
  };

  const removeCoverage = (id) => {
    const updatedValue = selectedCoverage.filter((item) => {
      if (item.value !== id) {
        return item;
      }
      return null;
    });
    dispatch(change('add_opportunity_form', 'coverage', updatedValue));
  };

  useEffect(() => {
    (async () => {
      await handleLoadAccountOptions();
      await handleLoadContactOptions();
      if (initialValues.account) {
        dispatch(change('add_opportunity_form', 'account', initialValues.account));
      }
      if (initialValues.campaign) {
        dispatch(change('add_opportunity_form', 'campaign', initialValues.campaign));
      }
      if (initialValues.sequence) {
        dispatch(change('add_opportunity_form', 'sequence', initialValues.sequence));
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (formValues && formValues.account && formValues.account.value) {
        await handleLoadOutletOptions();
        await handleLoadCampaignOptions();
        await handleLoadCoverageOptions();
        if (!initialValues) {
          dispatch(change('add_opportunity_form', 'campaign', ''));
          dispatch(change('add_opportunity_form', 'sequence', ''));
          dispatch(change('add_opportunity_form', 'coverage', []));
        }
      }
    })();
  }, [formValues && formValues.account && formValues.account.value]);

  useEffect(() => {
    (async () => {
      if (formValues && formValues.campaign && formValues.campaign.value) {
        await handleLoadSequenceOptions();
        if (!initialValues) {
          dispatch(change('add_opportunity_form', 'sequence', ''));
        }
      }
    })();
  }, [formValues && formValues.campaign && formValues.campaign.value]);
  const { fetchCount } = useCountContextCountRotting();

  const handleSubmit = async () => {
    setShowWarning(false);
    const submitFunc = isEdit ? editOpportunityAction : createOpportunityAction;

    await submitFunc({
      id: isEdit,
      title: formValues.title,
      stage: formValues.stage.value,
      circle_back_date: formValues.circle_back_date
        ? moment(formValues.circle_back_date).local().format(DATE_FORMAT)
        : null,
      contact_id: formValues.contact.value,
      outlet_id: formValues.outlet ? formValues.outlet.value : null,
      account_id: formValues.account.value,
      sequence_id: formValues.sequence ? formValues.sequence.value : null,
      mention_ids: formValues.coverage ? formValues.coverage.map(obj => obj.value) : [],
      notes: formValues.notes,
    });
    await fetchCount();
    toggle(true);
  };
  const handlePreSubmit = async (event) => {
    event.preventDefault();
    const mentionId = !isEmpty(formValues.coverage) ? last(formValues.coverage).value : null;
    if (!mentionId) {
      await handleSubmit();
      return;
    }

    const { data } = await axios.get(apiGetMention(mentionId), { withCredentials: true });

    if (get(formValues, 'campaign.value', null) !== get(data, 'campaign.id', null) &&
      get(formValues, 'sequence.value', null) !== get(data, 'sequence.id', null)) {
      setShowWarning(true);
    } else {
      await handleSubmit();
    }
  };

  const submitLabel = isEdit ? 'Save' : 'Add';
  const isLoading = isLoadingProps;
  return (
    <>
      <form onSubmit={handlePreSubmit} className={classes.disable}>
        {isLoading && <div className="loader"><LoadingIcon /></div>}

        {isEdit &&
          <p className={classes.lastUpdate}>
            {`Last updated: ${initialValues.modifiedBy ? initialValues.modifiedBy : ''} ${
              moment(initialValues.updatedDate).local().format(LAST_UPDATED)}`}
          </p>
        }
        {/* eslint-disable jsx-a11y/label-has-for */}
        <fieldset className={classes.group}>
          <Label>Title *</Label>
          <Field
            maxLength="255"
            name="title"
            component={RenderFormInput}
            type="text"
            onBlur={handleBlur}
            disabled={readOnly}
            validate={[requiredValidator]}
          />
          {/* eslint-disable jsx-a11y/label-has-for */}
          <Label>Stage *</Label>
          <Field
            name="stage"
            validate={[requiredOptionValidator]}
            component={RenderSelectInput}
            options={STAGE_OPTIONS}
            onBlur={handleBlur}
            disabled={readOnly}
          />
          {config.opportunityRotting && (
            <>
              <Label>Circle Back</Label>
              <Field
                className="date-picker"
                name="circle_back_date"
                props={{ className: classes.nestedInput }}
                component={RenderDateTimePicker}
                minDate={new Date()}
                dateFormat="MMM d, yyyy"
                showTimeSelect={false}
                disabled={readOnly}
              />
            </>
          )}
        </fieldset>
        <span className={classes.groupTitle}>Campaign</span>
        <fieldset className={classes.group}>
          {/* eslint-disable jsx-a11y/label-has-for */}
          <Label>Account *</Label>
          <Field
            name="account"
            component={RenderSelectInput}
            onBlur={handleBlur}
            validate={[requiredValidator]}
            options={resultsToOptions(accounts.results, 'name') || []}
            handleChange={(data) => {
              dispatch(change('add_opportunity_form', 'account', data));
              dispatch(change('add_opportunity_form', 'outlet', ''));
              dispatch(change('add_opportunity_form', 'campaign', ''));
              dispatch(change('add_opportunity_form', 'sequence', ''));
            }}
            onInputChange={input => handleLoadAccountOptions(input)}
            disabled={readOnly}
          />
          <div className={classes.twoColumnGroup}>
            <div className={classes.selectGroup}>
              {/* eslint-disable jsx-a11y/label-has-for */}
              <Label>Campaign</Label>
              <Field
                name="campaign"
                component={RenderSelectInput}
                options={resultsToOptions(campaigns.results, 'name') || []}
                onInputChange={input => handleLoadCampaignOptions(input)}
                onBlur={handleBlur}
                disabled={readOnly || (formValues && isEmpty(formValues.account))}
              />
            </div>
            <div className={classes.selectGroup}>
              {/* eslint-disable jsx-a11y/label-has-for */}
              <Label>Sequence</Label>
              <Field
                name="sequence"
                onBlur={handleBlur}
                component={RenderSelectInput}
                options={resultsToOptions(sequences.results, 'name') || []}
                onInputChange={input => handleLoadSequenceOptions(input)}
                disabled={readOnly || (formValues && isEmpty(formValues.campaign))}
              />
            </div>
          </div>
        </fieldset>
        <span className={classes.groupTitle}>Outlet</span>
        <fieldset className={classes.group}>
          {/* eslint-disable jsx-a11y/label-has-for */}
          <Label>Contact *</Label>
          <Field
            name="contact"
            validate={[requiredOptionValidator]}
            component={RenderSelectInput}
            options={resultsToOptions(contacts.results, getContactLabel)}
            onBlur={handleBlur}
            onInputChange={input => handleLoadContactOptions(input)}
            disabled={readOnly}
          />
          <div className={classes.twoColumnGroup}>
            <div className={classes.selectGroup}>
              {/* eslint-disable jsx-a11y/label-has-for */}
              <Label>Outlet *</Label>
              <Field
                name="outlet"
                onBlur={handleBlur}
                component={InfiniteScrollerSelect}
                options={resultsToOptions(outlets.results, getOutletNameUrl) || []}
                onInputChange={input => handleLoadOutletOptions({ search: input })}
                disabled={readOnly || (formValues && isEmpty(formValues.account))}
                validate={[requiredValidator]}
                scrollToBottom={() => {
                  if (outlets.next) handleLoadOutletOptions({ url: outlets.next });
                }}
              />
            </div>
          </div>
        </fieldset>
        <span className={classes.groupTitle}>Notes</span>
        <fieldset className={classes.group}>
          <Field
            maxLength="750"
            name="notes"
            component={RenderFormInput}
            displayProperty="none"
            type="text"
            multiline
            placeholder="add note..."
            onBlur={handleBlur}
            disabled={readOnly}
            className={classes.noteStyle}
          />
        </fieldset>
        <span className={classes.groupTitle}>Coverage</span>
        <fieldset className={classes.group}>
          <Field
            maxLength="255"
            name="coverage"
            component={RenderSelectInput}
            displayProperty="none"
            isMulti
            options={resultsToOptions(coverages.results, 'article_title') || []}
            onBlur={handleBlur}
            onInputChange={input => handleLoadCoverageOptions({ search: input })}
            disabled={readOnly || (formValues && isEmpty(formValues.account))}
          />
          <Label>Linked coverage</Label>
          {selectedCoverage && selectedCoverage.length > 0 ? <>{selectedCoverage.map((sel, index) => <><SelectedCoverage
            data={sel}
            logo={coverages && coverages.length > 0 ?
              coverages.results.find(coverage => coverage.id === sel.value).outlet.logo : null}
            removeCoverage={removeCoverage}
            readOnly={readOnly}
          />
            {selectedCoverage.length - 1 > index ? <hr style={{ margin: 0 }} /> : <> </>} </>)}
          </> : <div className={classes.coverageBlock}>No coverages</div>}
        </fieldset>
        {!readOnly &&
          <div className={classes.btnContainer}>
            <Button
              type="button"
              color="secondary"
              variant="text"
              onClick={toggle}
            >Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              disabled={pristine || submitting || invalid}
            >{submitLabel}
            </Button>
          </div>
        }
      </form>
      <ConfirmationModal
        open={showWarning}
        title="Warning"
        size="medium"
        confirmBtnColor="primaryCTA"
        onCancelClick={() => setShowWarning(false)}
        onConfirmClick={handleSubmit}
        onBackdropClick={() => setShowWarning(false)}
        showCancel={false}
      >The outlet, campaign and/or sequence on the opportunity does not match the coverage.<br />
      It is advised to have at least the campaign and sequence match,
      you can review the details on the coverage on the coverage tab.
      </ConfirmationModal>
    </>
  );
};


OpportunityForm.propTypes = {
  classes: PropTypes.element.isRequired,
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  invalid: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  formValues: PropTypes.element,
  initialValues: PropTypes.element,
  createOpportunityAction: PropTypes.func.isRequired,
  editOpportunityAction: PropTypes.func.isRequired,
  accounts: PropTypes.element.isRequired,
  campaigns: PropTypes.element.isRequired,
  sequences: PropTypes.element.isRequired,
  coverages: PropTypes.element.isRequired,
  outlets: PropTypes.element.isRequired,
  contacts: PropTypes.element.isRequired,
  getAccountsAction: PropTypes.func.isRequired,
  getCampaignsAction: PropTypes.func.isRequired,
  getSequencesAction: PropTypes.func.isRequired,
  getOutletsAction: PropTypes.func.isRequired,
  getCoverageAction: PropTypes.func.isRequired,
  getContactsAction: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  selectedCoverage: PropTypes.element.isRequired,
  readOnly: PropTypes.bool,
  config: PropTypes.shape({
    opportunityRotting: PropTypes.bool,
  }).isRequired,
  isLoading: PropTypes.bool,
};

OpportunityForm.defaultProps = {
  formValues: null,
  initialValues: {},
  readOnly: false,
  isLoading: false,
};
const selector = formValueSelector('add_opportunity_form');

export default compose(
  connect(
    (state, props) => {
      let initialValues = {
        title: null,
        stage: STAGE_OPTIONS[0].value,
        circle_back_date: null,
        account: null,
        campaign: null,
        sequence: null,
      };
      if (!isEmpty(props.initialValues)) {
        initialValues = {
          ...initialValues,
          ...props.initialValues,
        };
      }

      return {
        outlets: state.outlets,
        accounts: state.accounts,
        campaigns: state.campaigns,
        sequences: state.sequences,
        coverages: state.mentions,
        config: state.config,
        contacts: state.dropdownContacts,
        formValues: getFormValues('add_opportunity_form')(state),
        selectedCoverage: selector(state, 'coverage'),
        initialValues,
      };
    },
    {
      getContactsAction: getContacts,
      getOutletsAction: getOutlets,
      getAccountsAction: getAccounts,
      getCampaignsAction: getCampaigns,
      getSequencesAction: getSequences,
      getCoverageAction: getMentions,
      createOpportunityAction: createOpportunity,
      editOpportunityAction: updateOpportunity,
    },
  ),
  withStyles(styles),
  reduxForm({
    form: 'add_opportunity_form',
  }),
)(OpportunityForm);
